/**
 * WorkSurfacePageConnected — Data-connected Work Surface for portal.
 *
 * Wires up the WorkSurfacePage component from @ferp/workflow-forms
 * with real data queries from the portal's GraphQL layer.
 *
 * DRR-256: Universal entry point for frontline staff (USER layer).
 * DRR-055: Dumb frontend — terminology from settings bundle.
 */

import { useMemo } from 'react';
import { WorkSurfacePage } from '@ferp/workflow-forms';
import { TabulatorPrimitiveManager } from '@/components/primitives/TabulatorPrimitiveManager';
import {
  useMySubjectsQuery,
  useGetSettingNavigationQuery,
} from '@/generated/graphql';

export function WorkSurfacePageConnected() {
  // Load setting bundle for subject_label
  const { data: settingsData } = useGetSettingNavigationQuery();
  const subjectLabel = settingsData?.settingBundle?.subjectLabel ?? 'Subject';

  // Load subjects via scope-filtered mySubjects query
  const {
    data: subjectsData,
    loading: subjectsLoading,
    error: subjectsError,
  } = useMySubjectsQuery();

  // Build subjects list
  const subjects = useMemo(() => {
    return (
      subjectsData?.mySubjects?.map((s) => ({
        id: s.id,
        name: s.name,
        actorType: s.actorType,
      })) ?? []
    );
  }, [subjectsData]);

  // Load tasks for all subjects (batched)
  // TODO: Optimize with a dedicated backend query that returns counts per subject
  const tasksBySubject = useMemo(() => {
    const map = new Map<string, Array<{ id: string; status: string; priority: string; subjectActorId?: string }>>();
    // For now, we'll initialize empty maps - tasks will be loaded on focus
    for (const s of subjects) {
      map.set(s.id, []);
    }
    return map;
  }, [subjects]);

  return (
    <WorkSurfacePage
      subjectLabel={subjectLabel}
      subjects={subjects}
      subjectsLoading={subjectsLoading}
      subjectsError={subjectsError?.message}
      tasksBySubject={tasksBySubject}
      tasksLoading={false}
      TabulatorComponent={TabulatorPrimitiveManager}
    />
  );
}
